import { ElevatedSurface, LabelChip } from "@carbon6/c6-frontend-design";
import { CopyButton } from "@/shared/ui/CopyButton";
import { formatBytes } from "@/shared/utils/format/bytes";
import { formatDateTime } from "@/shared/utils/datetime/format";
import type { Probe } from "../../domain/probe";
import { shortDigest } from "../../feature/datasetFormatters";
import styles from "./DetailCard.module.css";
import chipStyles from "./ProbeEvidenceCard.module.css";

type ProbeEvidenceCardProps = {
  probe: Probe;
};

/** 확보한 샘플의 증거 — 어느 시점의 바이트를 무엇으로 골랐는지. */
export function ProbeEvidenceCard({ probe }: ProbeEvidenceCardProps) {
  const { manifestDigest, revision, sampleRef, selectedSourceIds } = probe;

  return (
    <ElevatedSurface as="section" className={styles.card} elevation={1} hoverElevation={false}>
      <h3 className={styles.title}>확보한 샘플</h3>
      <dl className={styles.list}>
        <dt className={styles.term}>선택한 episode</dt>
        <dd className={styles.value}>
          <SelectedEpisodes selectedSourceIds={selectedSourceIds} />
        </dd>

        {revision === null ? null : (
          <>
            <dt className={styles.term}>Revision</dt>
            <dd className={styles.value}>
              {shortDigest(revision.identifier)}
              <CopyButton label="Revision 식별자" value={revision.identifier} />
            </dd>

            <dt className={styles.term}>원본 규모</dt>
            <dd className={styles.value}>
              {revision.fileCount}개 · {formatBytes(revision.totalBytes)}
            </dd>

            <dt className={styles.term}>열거 시각</dt>
            <dd className={styles.value}>{formatDateTime(revision.listedAtMs)}</dd>
          </>
        )}

        <dt className={styles.term}>샘플 위치</dt>
        <dd className={styles.value}>
          {sampleRef === null ? "—" : sampleRef}
          {sampleRef === null ? null : <CopyButton label="샘플 위치" value={sampleRef} />}
        </dd>

        <dt className={styles.term}>manifest digest</dt>
        <dd className={styles.value}>
          {manifestDigest === null ? "—" : shortDigest(manifestDigest)}
          {manifestDigest === null ? null : (
            <CopyButton label="manifest digest" value={manifestDigest} />
          )}
        </dd>
      </dl>
      <p className={styles.caption}>
        샘플 다운로드는 아직 제공하지 않습니다. 현재는 저장소 좌표만 표시합니다.
      </p>
    </ElevatedSurface>
  );
}

/**
 * 세 상태가 서로 다른 뜻이라 한 문구로 합치지 않는다 — `null` 은 아직 고르지 않음,
 * `[]` 는 고른 것이 없음, 값은 그 목록이다(규칙 §6).
 */
function SelectedEpisodes({ selectedSourceIds }: { selectedSourceIds: readonly string[] | null }) {
  if (selectedSourceIds === null) {
    return <>선택 정보를 아직 받지 못했습니다.</>;
  }
  if (selectedSourceIds.length === 0) {
    return <>선택된 episode 가 없습니다.</>;
  }

  return (
    <span className={chipStyles.chips}>
      {selectedSourceIds.map((sourceId, index) => (
        <LabelChip key={sourceId} color={index} size="sm" variant="outline">
          {sourceId}
        </LabelChip>
      ))}
    </span>
  );
}
